import { useMutationMoveResourcesById } from '../mutations/useMutationMoveResourcesById.js';
import React__default from 'react';
import { toast } from '../../store/alerts.js';
import { isObjectsDroppableToResource } from '../../types/draggable.js';
import { isTruthy } from '@fabric-app/utils/guards';

const useDroppableAsTargetToResourceMove = (ref, resource, options) => {
  const enabled = options?.enabled ?? true;
  const { mutate: mutateMoveResourcesById } = useMutationMoveResourcesById();
  const [isDraggingItemsOver, setDraggingItemsOver] = React__default.useState(false);
  React__default.useEffect(() => {
    const htmlEl = ref?.current;
    if (!enabled || !htmlEl || !resource) {
      return;
    }
    const { id: targetResourceId } = resource;
    const onDragOver = (event) => {
      if (!isObjectsDroppableToResource(targetResourceId, event.detail)) {
        return;
      }
      event.stopPropagation();
      setDraggingItemsOver(true);
    };
    const onDragOut = () => {
      setDraggingItemsOver(false);
    };
    const onDragDrop = async (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (!isObjectsDroppableToResource(targetResourceId, event.detail)) {
        toast({
          content: "Can't move. One of the items you're trying to move is destination folder."
        });
        return;
      }
      setDraggingItemsOver(false);
      if (!event.detail.objectParentId) {
        return;
      }
      const resourceToMoveIds = Array.from(new Set([
        ...event.detail.objectIds ?? [],
        event.detail.objectId
      ].filter(isTruthy)));
      mutateMoveResourcesById({
        resourceIds: resourceToMoveIds,
        newParent: resource,
        action: options?.action ?? "drop-into-folder"
      }, {
        onSuccess: () => {
          const acceptEvent = new CustomEvent("object-drag-drop-accepted", {
            detail: {
              objectIds: resourceToMoveIds
            }
          });
          window.dispatchEvent(acceptEvent);
        }
      });
    };
    htmlEl.addEventListener("object-drag-over", onDragOver);
    htmlEl.addEventListener("object-drag-out", onDragOut);
    htmlEl.addEventListener("object-drag-drop", onDragDrop);
    return () => {
      htmlEl.removeEventListener("object-drag-over", onDragOver);
      htmlEl.removeEventListener("object-drag-out", onDragOut);
      htmlEl.removeEventListener("object-drag-drop", onDragDrop);
    };
  }, [
    ref,
    setDraggingItemsOver,
    resource,
    enabled,
    mutateMoveResourcesById,
    options?.action
  ]);
  const styles = React__default.useMemo(() => {
    return isDraggingItemsOver ? {
      border: "1px solid rgba(42, 55, 238, 0.2)",
      background: "rgba(42, 55, 238, 0.1)"
    } : {};
  }, [
    isDraggingItemsOver
  ]);
  const droppableProps = React__default.useMemo(() => {
    return {
      ["data-drag-over"]: isDraggingItemsOver,
      ["data-droppable"]: enabled
    };
  }, [
    isDraggingItemsOver,
    enabled
  ]);
  return {
    isDraggingItemsOver,
    droppableProps,
    styles
  };
};

export { useDroppableAsTargetToResourceMove };
//# sourceMappingURL=useDroppableAsTargetToResourceMove.js.map
